import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@monbits/ui";
import * as React from "react";
import { TwoFactorVerification } from "./two-factor-verification";

interface TwoFactorModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onVerify: (code: string) => Promise<boolean>;
	title?: string;
	description?: string;
	showBackupOption?: boolean;
}

export const TwoFactorModal: React.FC<TwoFactorModalProps> = ({
	open,
	onOpenChange,
	onVerify,
	title = "Verify Your Identity",
	description = "This action requires two-factor authentication",
	showBackupOption = true,
}) => {
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const handleVerify = async (code: string): Promise<boolean> => {
		setIsSubmitting(true);
		try {
			const success = await onVerify(code);
			if (success) {
				onOpenChange(false);
			}
			return success;
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancel = () => {
		if (!isSubmitting) {
			onOpenChange(false);
		}
	};

	const handleOpenChange = (open: boolean) => {
		// Prevent closing during submission
		if (!open && isSubmitting) {
			return;
		}
		onOpenChange(open);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>
				<TwoFactorVerification
					onVerify={handleVerify}
					onCancel={handleCancel}
					isSubmitting={isSubmitting}
					showBackupOption={showBackupOption}
					description="Enter the code from your authenticator app"
					submitLabel="Verify"
				/>
			</DialogContent>
		</Dialog>
	);
};

export const useTwoFactorModal = () => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [resolver, setResolver] = React.useState<{
		resolve: (value: boolean) => void;
	} | null>(null);

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			setIsOpen(open);
			if (!open && resolver) {
				resolver.resolve(false);
				setResolver(null);
			}
		},
		[resolver],
	);

	const ModalComponent = React.useCallback(
		({
			onVerify,
			title,
			description,
		}: {
			onVerify: (code: string) => Promise<boolean>;
			title?: string;
			description?: string;
		}) => (
			<TwoFactorModal
				open={isOpen}
				onOpenChange={handleOpenChange}
				onVerify={async (code: string) => {
					const success = await onVerify(code);
					if (success && resolver) {
						resolver.resolve(true);
						setResolver(null);
					}
					return success;
				}}
				title={title}
				description={description}
			/>
		),
		[isOpen, handleOpenChange, resolver],
	);

	return {
		isOpen,
		open: () => setIsOpen(true),
		close: () => handleOpenChange(false),
		TwoFactorModal: ModalComponent,
	};
};
